import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { api, Summary, TrendData, SourceData, SourceLookupResult, WeeklyDigest, ActionItem, DismissedActionItem } from "@/lib/api";
import type { Domain } from "@dmarc/shared";
import { Link, useNavigate } from "@tanstack/react-router";
import { dashboardRoute } from "@/routeTree.gen";
import { CopyButton } from "@/components/CopyButton";
import { useCurrentPlan } from "@/hooks/useCurrentPlan";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#22c55e", "#ef4444"];

export function DashboardPage() {
  const { currentOrganization } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { domain: selectedDomainId, days: daysParam } = dashboardRoute.useSearch();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Determine if 30d view is allowed based on plan (free users only get 7d)
  const { plan, isFree, isPaid } = useCurrentPlan();
  const canView30Days = isPaid;

  // Default to 7d if plan doesn't allow 30d, otherwise respect URL param
  const days = (daysParam === 7 || daysParam === 30)
    ? (daysParam === 30 && !canView30Days ? 7 : daysParam)
    : 7;

  const [expandedIp, setExpandedIp] = useState<string | null>(null);
  const [showReviewedTab, setShowReviewedTab] = useState(false);
  const [dismissingItem, setDismissingItem] = useState<ActionItem | null>(null);
  const [resolvingItem, setResolvingItem] = useState<string | null>(null);
  const [resolveResult, setResolveResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showSecurityGateways, setShowSecurityGateways] = useState(false);
  const [showAllActionItems, setShowAllActionItems] = useState(false);

  const { data: domains, isLoading: domainsLoading } = useQuery<Domain[]>({
    queryKey: ["domains", currentOrganization?.id],
    queryFn: () => api.domains.list(currentOrganization!.id),
    enabled: !!currentOrganization,
  });

  const { data: summary, isLoading: summaryLoading } = useQuery<Summary>({
    queryKey: ["reports-summary", currentOrganization?.id, days, selectedDomainId],
    queryFn: () => api.reports.summary({
      organizationId: currentOrganization!.id,
      days,
      domainId: selectedDomainId,
    }),
    enabled: !!currentOrganization,
  });

  const { data: trends } = useQuery<TrendData[]>({
    queryKey: ["reports-trends", currentOrganization?.id, days, selectedDomainId],
    queryFn: () => api.reports.trends({
      organizationId: currentOrganization!.id,
      days,
      domainId: selectedDomainId,
    }),
    enabled: !!currentOrganization,
  });

  const { data: sources } = useQuery<SourceData[]>({
    queryKey: ["reports-sources", currentOrganization?.id, days, selectedDomainId],
    queryFn: () => api.reports.sources({
      organizationId: currentOrganization!.id,
      limit: 10,
      days,
      domainId: selectedDomainId,
    }),
    enabled: !!currentOrganization,
  });

  // Get weekly digest for action items
  const { data: digest, isLoading: digestLoading } = useQuery<WeeklyDigest>({
    queryKey: ["weekly-digest", currentOrganization?.id, selectedDomainId],
    queryFn: () => api.reports.digest(currentOrganization!.id, selectedDomainId),
    enabled: !!currentOrganization,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  // Get subscription/usage data for limit warnings
  const { data: subscription } = useQuery({
    queryKey: ["subscription", currentOrganization?.id],
    queryFn: () => api.billing.getSubscription(currentOrganization!.id),
    enabled: !!currentOrganization,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Calculate usage percentage
  const usagePercent = subscription
    ? Math.round((subscription.monthlyMessageCount / subscription.messageLimit) * 100)
    : 0;
  const isNearLimit = usagePercent >= 80;
  const isOverLimit = usagePercent >= 100;

  // Get IPs with failures for lookup
  const failedSourceIps = sources?.filter(s => s.failCount > 0).map(s => s.sourceIp) || [];

  // Lookup source information for sources with failures
  const { data: sourceLookups } = useQuery<Record<string, SourceLookupResult>>({
    queryKey: ["source-lookups-dashboard", failedSourceIps],
    queryFn: () => api.reports.lookupSources(failedSourceIps),
    enabled: failedSourceIps.length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Get dismissed action items
  const { data: dismissedItems } = useQuery<DismissedActionItem[]>({
    queryKey: ["dismissed-action-items", currentOrganization?.id],
    queryFn: () => api.reports.getDismissedActionItems(currentOrganization!.id),
    enabled: !!currentOrganization,
  });

  // Resolve action item mutation
  const resolveMutation = useMutation({
    mutationFn: (item: ActionItem) => api.reports.resolveActionItem({
      organizationId: currentOrganization!.id,
      actionType: item.type,
      actionKey: item.sourceIp || "policy",
      serviceName: item.serviceName,
      spfInclude: item.spfInclude,
      domain: item.domain,
    }),
    onMutate: (item) => setResolvingItem(item.sourceIp || "policy"),
    onSettled: () => setResolvingItem(null),
    onSuccess: async (result) => {
      if (result.verified) {
        // Force refetch to update the UI immediately (bypasses staleTime)
        await queryClient.refetchQueries({ queryKey: ["weekly-digest", currentOrganization?.id] });
        await queryClient.refetchQueries({ queryKey: ["dismissed-action-items", currentOrganization?.id] });
      }
      setResolveResult({ success: result.verified, message: result.message });
      setTimeout(() => setResolveResult(null), 5000);
    },
  });

  // Dismiss action item mutation
  const dismissMutation = useMutation({
    mutationFn: ({ item, reason }: { item: ActionItem; reason: "spoofing" | "not_my_service" | "other" }) =>
      api.reports.dismissActionItem({
        organizationId: currentOrganization!.id,
        actionType: item.type,
        actionKey: item.sourceIp || "policy",
        reason,
      }),
    onSuccess: async () => {
      // Force refetch to update the UI immediately (bypasses staleTime)
      await queryClient.refetchQueries({ queryKey: ["weekly-digest", currentOrganization?.id] });
      await queryClient.refetchQueries({ queryKey: ["dismissed-action-items", currentOrganization?.id] });
      setDismissingItem(null);
    },
  });

  // Restore action item mutation
  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.reports.restoreActionItem(id, currentOrganization!.id),
    onSuccess: async () => {
      // Force refetch to update the UI immediately (bypasses staleTime)
      await queryClient.refetchQueries({ queryKey: ["weekly-digest", currentOrganization?.id] });
      await queryClient.refetchQueries({ queryKey: ["dismissed-action-items", currentOrganization?.id] });
    },
  });

  // Filter action items into regular and security gateway items
  // Must be before early return to satisfy rules of hooks
  const { regularActionItems, securityGatewayItems } = useMemo(() => {
    const items = digest?.actionItems || [];
    const regular: ActionItem[] = [];
    const gateway: ActionItem[] = [];

    for (const item of items) {
      if (item.sourceIp && sourceLookups?.[item.sourceIp]?.matchedService?.category === "security_gateway") {
        gateway.push(item);
      } else {
        regular.push(item);
      }
    }

    return { regularActionItems: regular, securityGatewayItems: gateway };
  }, [digest?.actionItems, sourceLookups]);

  const pieData = useMemo(() => summary
    ? [
        { name: "Passed", value: summary.passedMessages },
        { name: "Failed", value: summary.failedMessages },
      ]
    : [], [summary]);

  const formattedTrends = useMemo(() => trends?.map((t) => ({
    ...t,
    date: new Date(t.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  })), [trends]);

  if (!currentOrganization) {
    return (
      <div className="text-center text-muted-foreground">
        No organization selected
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Usage limit warning */}
      {subscription && isNearLimit && isFree && (
        <div className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${
          isOverLimit
            ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50"
            : "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50"
        }`}>
          <div className="flex items-center gap-3">
            <svg className={`h-5 w-5 ${isOverLimit ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <span className={`text-sm ${isOverLimit ? "text-red-800 dark:text-red-200" : "text-amber-800 dark:text-amber-200"}`}>
              {isOverLimit
                ? `You've exceeded your monthly message limit (${subscription.monthlyMessageCount.toLocaleString()} / ${subscription.messageLimit.toLocaleString()}). Some features may be limited.`
                : `You've used ${usagePercent}% of your monthly message limit (${subscription.monthlyMessageCount.toLocaleString()} / ${subscription.messageLimit.toLocaleString()}).`
              }
            </span>
          </div>
          <Link
            to="/pricing"
            className={`shrink-0 text-sm font-medium underline hover:no-underline ${
              isOverLimit ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300"
            }`}
          >
            Upgrade now
          </Link>
        </div>
      )}

      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Welcome back</h1>
            <p className="mt-1 text-indigo-100">
              {currentOrganization.name} dashboard
            </p>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Domain filter */}
            <select
              value={selectedDomainId || ""}
              onChange={(e) => navigate({ to: "/dashboard", search: { domain: e.target.value || undefined, days } })}
              className="flex-1 sm:flex-none min-w-0 rounded-lg bg-white/20 pl-3 pr-9 py-2 text-sm font-medium text-white backdrop-blur-sm border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <option value="" className="text-gray-900">All domains</option>
              {domains?.map((d) => (
                <option key={d.id} value={d.id} className="text-gray-900">
                  {d.domain}
                </option>
              ))}
            </select>
            {/* Time range filter */}
            <div className="flex rounded-lg bg-white/20 p-1 backdrop-blur-sm border border-white/30">
              <button
                onClick={() => navigate({ to: "/dashboard", search: { domain: selectedDomainId, days: 7 } })}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  days === 7
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "text-white hover:bg-white/20"
                }`}
              >
                7d
              </button>
              <button
                onClick={() => {
                  if (canView30Days) {
                    navigate({ to: "/dashboard", search: { domain: selectedDomainId, days: 30 } });
                  } else {
                    setShowUpgradePrompt(true);
                  }
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  days === 30
                    ? "bg-white text-indigo-600 shadow-sm"
                    : canView30Days
                      ? "text-white hover:bg-white/20"
                      : "text-white/50 hover:text-white hover:bg-white/10 cursor-pointer"
                }`}
                title={!canView30Days ? "Click to upgrade" : undefined}
              >
                30d
                {!canView30Days && (
                  <svg className="inline-block ml-1 w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="page-header-decor" />
        <div className="page-header-decor-alt" />
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        <Link to="/domains" className="stat-card cursor-pointer hover:border-teal-200 dark:hover:border-teal-800">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-teal">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Domains</div>
              <div className="stat-value">
                {domainsLoading ? "..." : domains?.length || 0}
              </div>
            </div>
          </div>
        </Link>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-primary">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Messages ({days}d)</div>
              <div className="stat-value">
                {summaryLoading ? "..." : summary?.totalMessages?.toLocaleString() || 0}
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-emerald">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Pass Rate</div>
              <div className="stat-value">
                {summaryLoading ? "..." : summary?.totalMessages ? (
                  <span className={summary.passRate >= 90 ? "text-success" : summary.passRate >= 70 ? "text-warning" : "text-error"}>
                    {summary.passRate}%
                  </span>
                ) : "--"}
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-amber">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Reports</div>
              <div className="stat-value">
                {summaryLoading ? "..." : summary?.totalReports || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Summary & Action Items */}
      {digestLoading ? (
        <div className="grid gap-6 lg:grid-cols-2 min-h-[300px]">
          <div className="card animate-pulse">
            <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
            <div className="space-y-3">
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          </div>
          <div className="card animate-pulse">
            <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      ) : digest && digest.hasData && (digest.actionItems.length > 0 || digest.newSenders.length > 0) ? (
        <div className="grid gap-6 lg:grid-cols-2 min-h-[300px]">
          {/* Action Items */}
          {(digest.actionItems.length > 0 || (dismissedItems && dismissedItems.length > 0)) && (
            <div className="card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setShowReviewedTab(false)}
                    className={`text-lg font-semibold pb-1 border-b-2 transition-colors ${
                      !showReviewedTab
                        ? "border-indigo-600 text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Action Required
                    {regularActionItems.length > 0 && (
                      <span className="ml-2 text-sm bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 px-2 py-0.5 rounded-full">
                        {regularActionItems.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setShowReviewedTab(true)}
                    className={`text-lg font-semibold pb-1 border-b-2 transition-colors ${
                      showReviewedTab
                        ? "border-indigo-600 text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Reviewed
                    {dismissedItems && dismissedItems.length > 0 && (
                      <span className="ml-2 text-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full">
                        {dismissedItems.length}
                      </span>
                    )}
                  </button>
                </div>
                <div className={`flex items-center gap-2 text-sm font-medium ${
                  digest.stats.trend === "up" ? "text-green-600" :
                  digest.stats.trend === "down" ? "text-red-600" : "text-gray-600"
                }`}>
                  {digest.stats.trend === "up" ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                    </svg>
                  ) : digest.stats.trend === "down" ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15" />
                    </svg>
                  )}
                  <span>
                    {digest.stats.trend === "up" ? `+${digest.stats.trendPercent}%` :
                     digest.stats.trend === "down" ? `-${digest.stats.trendPercent}%` :
                     "Stable"} vs last week
                  </span>
                </div>
              </div>

              {/* Resolve Result Feedback */}
              {resolveResult && (
                <div className={`mt-4 alert ${resolveResult.success ? "alert-success" : "alert-warning"}`}>
                  {resolveResult.message}
                </div>
              )}

              {/* Action Items Tab */}
              {!showReviewedTab && (
              <div className="mt-4 space-y-3">
                {regularActionItems.length === 0 && securityGatewayItems.length === 0 ? (
                  <p className="text-muted-foreground py-4 text-center">No action items. You're all caught up!</p>
                ) : (
                  <>
                    {(showAllActionItems ? regularActionItems : regularActionItems.slice(0, 1)).map((item: ActionItem, index: number) => (
                  <div
                    key={index}
                    className={`rounded-lg border p-4 ${
                      item.priority === "high" ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20" :
                      item.priority === "medium" ? "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20" :
                      "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/20"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                            item.priority === "high" ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300" :
                            item.priority === "medium" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" :
                            "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {item.priority}
                          </span>
                          {item.domain && (
                            <span className="inline-flex items-center rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300 px-2 py-0.5 text-xs font-medium">
                              {item.domain}
                            </span>
                          )}
                          <span className="font-medium">{item.title}</span>
                        </div>

                        {/* Context-aware guidance */}
                        {item.type === "failing_service" && item.serviceName && (
                          <div className="mt-3 space-y-3">
                            <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border border-green-200 dark:border-green-800">
                              <p className="text-sm font-medium text-green-700 dark:text-green-400">
                                If you use {item.serviceName}:
                              </p>
                              <p className="mt-1 text-sm text-muted-foreground">
                                Authorize it by adding this to your SPF record:
                              </p>
                              {item.spfInclude && (
                                <div className="mt-2 flex items-center gap-2">
                                  <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
                                    include:{item.spfInclude}
                                  </code>
                                  <CopyButton text={`include:${item.spfInclude}`} variant="button" />
                                </div>
                              )}
                              {item.dkimSetupUrl && (
                                <a
                                  href={item.dkimSetupUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-2 inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-500"
                                >
                                  View DKIM setup guide
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                  </svg>
                                </a>
                              )}
                            </div>

                            <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                If you don't use {item.serviceName}:
                              </p>
                              <p className="mt-1 text-sm text-muted-foreground">
                                This could be someone attempting to spoof your domain. Your DMARC policy is correctly
                                blocking these emails. No action needed — check your reports for more details.
                              </p>
                            </div>
                          </div>
                        )}

                        {item.type === "unknown_sender" && (
                          <div className="mt-3 p-3 rounded-lg bg-white dark:bg-gray-900 border">
                            <p className="text-sm text-muted-foreground">
                              We detected {item.emailCount.toLocaleString()} emails from an unrecognized source. This could be:
                            </p>
                            <ul className="mt-2 text-sm text-muted-foreground list-disc list-inside space-y-1">
                              <li>A legitimate service you use that we haven't identified</li>
                              <li>Someone attempting to spoof your domain (being blocked by DMARC)</li>
                            </ul>
                            <p className="mt-2 text-sm text-muted-foreground">
                              Check your DMARC reports to see which emails were sent from this IP.
                            </p>
                          </div>
                        )}

                        {item.type === "policy_recommendation" && (
                          <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                        )}

                        {/* Action buttons */}
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() => resolveMutation.mutate(item)}
                            disabled={resolvingItem === (item.sourceIp || "policy")}
                            className="btn btn-sm btn-primary"
                          >
                            {resolvingItem === (item.sourceIp || "policy") ? (
                              "Checking..."
                            ) : (
                              <>
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                </svg>
                                I Fixed This
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => setDismissingItem(item)}
                            className="btn btn-sm btn-secondary"
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Dismiss
                          </button>
                        </div>
                      </div>
                      {item.emailCount > 0 && (
                        <span className="text-sm text-muted-foreground whitespace-nowrap ml-4">{item.emailCount.toLocaleString()} emails</span>
                      )}
                    </div>
                  </div>
                    ))}

                    {/* See more/less button */}
                    {regularActionItems.length > 1 && (
                      <button
                        onClick={() => setShowAllActionItems(!showAllActionItems)}
                        className="w-full py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center justify-center gap-1"
                      >
                        {showAllActionItems ? (
                          <>
                            Show less
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                            </svg>
                          </>
                        ) : (
                          <>
                            See {regularActionItems.length - 1} more
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                          </>
                        )}
                      </button>
                    )}

                    {/* Security Gateway Items - Collapsed by default */}
                    {securityGatewayItems.length > 0 && (
                      <div className="mt-4">
                        <button
                          onClick={() => setShowSecurityGateways(!showSecurityGateways)}
                          className="flex items-center gap-2 w-full p-3 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/20 text-left hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                          <svg
                            className={`icon-sm text-blue-600 dark:text-blue-400 transition-transform ${showSecurityGateways ? 'rotate-90' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                          <svg className="icon-sm text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                          </svg>
                          <div className="flex-1">
                            <span className="font-medium text-blue-700 dark:text-blue-300">
                              Recipient Security ({securityGatewayItems.length})
                            </span>
                            <p className="text-sm text-blue-600/80 dark:text-blue-400/80">
                              Failures caused by recipients' security tools — no action needed
                            </p>
                          </div>
                        </button>

                        {showSecurityGateways && (
                          <div className="mt-3 space-y-3">
                            {securityGatewayItems.map((item: ActionItem, index: number) => {
                              const lookup = item.sourceIp ? sourceLookups?.[item.sourceIp] : null;
                              return (
                                <div
                                  key={`gateway-${index}`}
                                  className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/20 p-4"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="badge badge-info">No Action Needed</span>
                                        {item.domain && (
                                          <span className="badge badge-neutral">{item.domain}</span>
                                        )}
                                        <span className="font-medium">{lookup?.matchedService?.name || item.serviceName}</span>
                                      </div>
                                      <div className="mt-3 p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                        <p className="text-sm text-muted-foreground">
                                          <strong>{lookup?.matchedService?.name || item.serviceName}</strong> is an email security tool used by the people receiving your emails. When they scan messages, it breaks authentication signatures. This is normal — your email was sent correctly.
                                        </p>
                                        {lookup?.ptr && (
                                          <p className="text-sm text-muted-foreground mt-2">
                                            <span className="font-medium">PTR:</span>{' '}
                                            <code className="font-mono">{lookup.ptr}</code>
                                          </p>
                                        )}
                                        <Link
                                          to="/guides/recipient-security-gateways"
                                          className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 hover:text-blue-500"
                                        >
                                          Learn more
                                          <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                          </svg>
                                        </Link>
                                      </div>
                                      <div className="mt-3">
                                        <button
                                          onClick={() => setDismissingItem(item)}
                                          className="btn btn-sm btn-secondary"
                                        >
                                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                          Dismiss
                                        </button>
                                      </div>
                                    </div>
                                    {item.emailCount > 0 && (
                                      <span className="text-sm text-muted-foreground whitespace-nowrap ml-4">{item.emailCount.toLocaleString()} emails</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              )}

              {/* Reviewed Items Tab */}
              {showReviewedTab && (
                <div className="mt-4 space-y-3">
                  {!dismissedItems || dismissedItems.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-center">No reviewed items yet.</p>
                  ) : (
                    dismissedItems.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-lg border p-4 bg-gray-50 dark:bg-gray-800/20"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                                item.status === "resolved"
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
                                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                              }`}>
                                {item.status === "resolved" ? "Resolved" : "Dismissed"}
                              </span>
                              {item.domain && (
                                <span className="inline-flex items-center rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300 px-2 py-0.5 text-xs font-medium">
                                  {item.domain}
                                </span>
                              )}
                              <span className="font-medium capitalize">
                                {item.actionType.replace(/_/g, " ")}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {item.actionKey !== "policy" && `IP: ${item.actionKey}`}
                              {item.dismissReason && ` - ${item.dismissReason.replace(/_/g, " ")}`}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {item.resolvedByName && `By ${item.resolvedByName} on `}
                              {new Date(item.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => restoreMutation.mutate(item.id)}
                            disabled={restoreMutation.isPending}
                            className="btn btn-sm btn-secondary"
                          >
                            Restore
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* New Senders This Week */}
          {digest.newSenders.length > 0 && (
            <div className="card">
              <h2 className="text-lg font-semibold">New This Week</h2>
              <p className="text-sm text-muted-foreground">First time seeing these senders</p>
              <div className="mt-4">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="table-header">Source</th>
                      <th className="table-header text-right">Emails</th>
                      <th className="table-header text-right">Pass Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {digest.newSenders.map((sender, index) => (
                      <tr key={index} className="table-row">
                        <td className="table-cell font-medium">
                          {sender.serviceName || sender.sourceIp}
                        </td>
                        <td className="table-cell text-right">
                          {sender.emailCount.toLocaleString()}
                        </td>
                        <td className="table-cell text-right">
                          <span className={`font-medium ${
                            sender.passRate >= 95 ? "text-green-600" :
                            sender.passRate >= 80 ? "text-amber-600" : "text-red-600"
                          }`}>
                            {sender.passRate >= 95 ? "✓" : sender.passRate >= 80 ? "⚠" : "✗"} {sender.passRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Onboarding checklist - show until we have reports */}
      {!summaryLoading && summary?.totalReports === 0 && (() => {
        const hasDomain = domains && domains.length > 0;
        const hasVerified = domains?.some(d => d.verified);
        const hasDmarc = domains?.some(d => d.dmarcRecord);
        const hasReports = (summary?.totalReports ?? 0) > 0;

        const steps = [
          {
            key: "1",
            title: "Add your domain",
            desc: "Go to Domains and add your first domain",
            completed: hasDomain,
            link: "/domains",
            linkText: "Add Domain"
          },
          {
            key: "2",
            title: "Verify ownership",
            desc: "Add the TXT record to your DNS to prove ownership",
            completed: hasVerified,
            link: hasDomain ? "/domains" : undefined,
            linkText: "View DNS Records"
          },
          {
            key: "3",
            title: "Configure DMARC",
            desc: "Add the DMARC record with our rua address to receive reports",
            completed: hasDmarc,
            link: hasDomain ? "/domains" : undefined,
            linkText: "View DMARC Setup"
          },
          {
            key: "4",
            title: "Wait for reports",
            desc: "Reports typically arrive within 24-48 hours after DMARC is configured",
            completed: hasReports,
            isWaiting: hasDmarc && !hasReports
          },
        ];

        const completedCount = steps.filter(s => s.completed).length;
        const currentStep = steps.find(s => !s.completed);
        const progressPercent = (completedCount / steps.length) * 100;

        return (
          <div className="rounded-2xl border bg-gradient-to-br from-indigo-50 to-purple-50 p-8 dark:from-indigo-950/30 dark:to-purple-950/30">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Get Started</h2>
                <p className="mt-1 text-muted-foreground">
                  {completedCount === 0
                    ? "Complete these steps to start monitoring your email authentication"
                    : `${completedCount} of ${steps.length} steps completed`
                  }
                </p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{Math.round(progressPercent)}%</span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4 h-2 w-full rounded-full bg-indigo-100 dark:bg-indigo-900/50">
              <div
                className="h-2 rounded-full bg-indigo-600 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-4">
              {steps.map((item) => {
                const isCurrent = currentStep?.key === item.key;
                return (
                  <div
                    key={item.key}
                    className={`card relative ${isCurrent ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-gray-900' : ''} ${item.completed ? 'bg-green-50 dark:bg-green-950/20' : ''}`}
                  >
                    {/* Status indicator */}
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                      item.completed
                        ? 'bg-green-500 text-white'
                        : item.isWaiting
                          ? 'bg-amber-500 text-white animate-pulse'
                          : isCurrent
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {item.completed ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      ) : item.isWaiting ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        item.key
                      )}
                    </div>

                    <h3 className={`mt-3 font-semibold ${item.completed ? 'text-green-700 dark:text-green-400' : ''}`}>
                      {item.title}
                      {item.completed && <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-500">Done</span>}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">{item.desc}</p>

                    {/* Waiting message */}
                    {item.isWaiting && (
                      <div className="mt-3 rounded-lg bg-amber-100 px-3 py-2 dark:bg-amber-900/30">
                        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                          Waiting for your first report...
                        </p>
                      </div>
                    )}

                    {/* Action link */}
                    {item.link && !item.completed && (
                      <Link
                        to={item.link}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                      >
                        {item.linkText}
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Time expectation callout when waiting */}
            {hasDmarc && !hasReports && (
              <div className="mt-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
                <svg className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-300">Almost there!</p>
                  <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                    DMARC reports are sent by email providers (Google, Microsoft, etc.) once every 24 hours.
                    You should receive your first report within <strong>24-48 hours</strong> after adding the DMARC record.
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Charts - show when we have data */}
      {summary && summary.totalMessages > 0 && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Pass/Fail Pie Chart */}
          <div className="card">
            <h2 className="text-lg font-semibold">DMARC Compliance</h2>
            <p className="text-sm text-muted-foreground">Pass vs fail rate for the last {days} days</p>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-6 -mt-4">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                  <span className="text-sm text-muted-foreground">Passed ({summary.passedMessages?.toLocaleString()})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <span className="text-sm text-muted-foreground">Failed ({summary.failedMessages?.toLocaleString()})</span>
                </div>
              </div>
            </div>
          </div>

          {/* Trends Area Chart */}
          <div className="card">
            <h2 className="text-lg font-semibold">Email Volume Trends</h2>
            <p className="text-sm text-muted-foreground">Daily email authentication results</p>
            <div className="mt-4 h-64">
              {formattedTrends && formattedTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedTrends}>
                    <defs>
                      <linearGradient id="colorCompliant" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorNonCompliant" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorThreat" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" fontSize={12} stroke="#9ca3af" />
                    <YAxis fontSize={12} stroke="#9ca3af" />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="compliant"
                      stroke="#22c55e"
                      fillOpacity={1}
                      fill="url(#colorCompliant)"
                      name="Compliant"
                      strokeWidth={2}
                      stackId="1"
                    />
                    <Area
                      type="monotone"
                      dataKey="nonCompliant"
                      stroke="#f59e0b"
                      fillOpacity={1}
                      fill="url(#colorNonCompliant)"
                      name="Non-compliant"
                      strokeWidth={2}
                      stackId="1"
                    />
                    <Area
                      type="monotone"
                      dataKey="threat"
                      stroke="#ef4444"
                      fillOpacity={1}
                      fill="url(#colorThreat)"
                      name="Threat"
                      strokeWidth={2}
                      stackId="1"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  No trend data yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sending Sources Table */}
      {sources && sources.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold">Top Sending Sources</h2>
          <p className="text-sm text-muted-foreground">Servers sending email on behalf of your domains. Click on sources with failures to see recommendations.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="table">
              <thead>
                <tr className="table-header">
                  <th className="table-header-cell">Source IP</th>
                  <th className="table-header-cell">Organization</th>
                  <th className="table-header-cell text-right">Messages</th>
                  <th className="table-header-cell text-right">Passed</th>
                  <th className="table-header-cell text-right">Failed</th>
                  <th className="table-header-cell text-right">Pass Rate</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source, i) => {
                  const hasFailed = source.failCount > 0;
                  const isExpanded = expandedIp === source.sourceIp;
                  const lookup = sourceLookups?.[source.sourceIp];

                  return (
                    <>
                      <tr
                        key={i}
                        className={`table-row ${hasFailed ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                        onClick={() => hasFailed && setExpandedIp(isExpanded ? null : source.sourceIp)}
                      >
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            {hasFailed && (
                              <svg
                                className={`icon-sm text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                              </svg>
                            )}
                            <span className="font-mono text-sm">{source.sourceIp}</span>
                          </div>
                        </td>
                        <td className="table-cell text-muted-foreground">
                          {source.asnOrg || "-"}
                        </td>
                        <td className="table-cell text-right font-medium">{source.totalCount?.toLocaleString() ?? 0}</td>
                        <td className="table-cell text-right font-medium text-success">
                          {source.passCount?.toLocaleString() ?? 0}
                        </td>
                        <td className="table-cell text-right font-medium text-error">
                          {source.failCount?.toLocaleString() ?? 0}
                        </td>
                        <td className="table-cell text-right">
                          <span
                            className={`badge ${
                              (source.passRate ?? 0) >= 90
                                ? "badge-success"
                                : (source.passRate ?? 0) >= 70
                                ? "badge-warning"
                                : "badge-error"
                            }`}
                          >
                            {source.passRate ?? 0}%
                          </span>
                        </td>
                      </tr>
                      {/* Expanded recommendation row */}
                      {isExpanded && hasFailed && (
                        <tr key={`${i}-expanded`} className={lookup?.matchedService?.category === "security_gateway" ? "bg-blue-50 dark:bg-blue-950/20" : "bg-amber-50 dark:bg-amber-950/20"}>
                          <td colSpan={6} className="p-4">
                            <div className="flex items-start gap-4">
                              <div className={`p-2 rounded-lg ${lookup?.matchedService?.category === "security_gateway" ? "bg-blue-100 dark:bg-blue-900/30" : "bg-amber-100 dark:bg-amber-900/30"}`}>
                                {lookup?.matchedService?.category === "security_gateway" ? (
                                  <svg className="icon-md text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                                  </svg>
                                ) : (
                                  <svg className="icon-md text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 space-y-3">
                                {lookup?.matchedService?.category === "security_gateway" ? (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <span className="badge badge-info">No Action Needed</span>
                                      <span className="font-medium">{lookup.matchedService.name}</span>
                                    </div>
                                    <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                      <p className="text-sm text-muted-foreground">
                                        <strong>{lookup.matchedService.name}</strong> is an email security tool used by the people receiving your emails. When they scan messages, it breaks authentication signatures. This is normal — your email was sent correctly, and DMARC flagged the relay as expected. No action needed on your end.
                                      </p>
                                      {lookup.ptr && (
                                        <p className="text-sm text-muted-foreground mt-2">
                                          <span className="font-medium">PTR:</span>{' '}
                                          <code className="font-mono">{lookup.ptr}</code>
                                        </p>
                                      )}
                                      <Link
                                        to="/guides/recipient-security-gateways"
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 hover:text-blue-500"
                                      >
                                        Learn more about recipient security gateways
                                        <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                        </svg>
                                      </Link>
                                    </div>
                                  </>
                                ) : lookup?.matchedService ? (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">Identified as:</span>
                                      <span className="badge badge-primary">{lookup.matchedService.name}</span>
                                      <span className="text-sm text-muted-foreground">({lookup.matchedService.category})</span>
                                    </div>
                                    {lookup.recommendation && (
                                      <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                        <div className="flex items-center gap-2 mb-2">
                                          <svg className="icon-sm text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                          </svg>
                                          <span className="font-medium text-sm">Recommended Fix</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">{lookup.recommendation}</p>
                                        {lookup.matchedService.spfInclude && (
                                          <div className="mt-3 flex items-center gap-2">
                                            <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
                                              include:{lookup.matchedService.spfInclude}
                                            </code>
                                            <CopyButton text={`include:${lookup.matchedService.spfInclude}`} variant="button" />
                                          </div>
                                        )}
                                        {lookup.matchedService.dkimSetupUrl && (
                                          <a
                                            href={lookup.matchedService.dkimSetupUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-1 mt-3 text-sm text-indigo-600 hover:text-indigo-500"
                                          >
                                            View {lookup.matchedService.name} DKIM setup guide
                                            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                            </svg>
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : lookup?.asnOrg ? (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">Hosted on:</span>
                                      <span className="text-muted-foreground">{lookup.asnOrg}</span>
                                    </div>
                                    {lookup.ptr && (
                                      <div className="text-sm text-muted-foreground">
                                        <span className="font-medium">PTR:</span>{' '}
                                        <code className="font-mono">{lookup.ptr}</code>
                                      </div>
                                    )}
                                    {lookup.recommendation && (
                                      <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                        <p className="text-sm text-muted-foreground">{lookup.recommendation}</p>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-sm text-muted-foreground">
                                    <p>Unable to identify this sender. This could be:</p>
                                    <ul className="list-disc list-inside mt-2 space-y-1">
                                      <li>A legitimate service you use that we haven't identified yet</li>
                                      <li>An unauthorized sender attempting to spoof your domain</li>
                                    </ul>
                                    <p className="mt-2">Check if you recognize this IP address or contact your IT team.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold">Upgrade for More Data</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              The free plan retains data for 7 days. Upgrade to access up to 1 year of historical data:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span><strong>Starter:</strong> 90 days of data retention</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span><strong>Pro & above:</strong> 365 days of data retention</span>
              </li>
            </ul>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradePrompt(false)}
                className="btn btn-secondary flex-1"
              >
                Maybe Later
              </button>
              <Link
                to="/pricing"
                className="btn btn-primary flex-1 text-center"
                onClick={() => setShowUpgradePrompt(false)}
              >
                View Plans
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Dismiss Action Item Modal */}
      {dismissingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold">Dismiss Action Item</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Why are you dismissing "{dismissingItem.title}"?
            </p>
            <div className="mt-4 space-y-2">
              <button
                onClick={() => dismissMutation.mutate({ item: dismissingItem, reason: "spoofing" })}
                disabled={dismissMutation.isPending}
                className="w-full text-left px-4 py-3 rounded-lg border hover:bg-muted transition-colors"
              >
                <div className="font-medium">This is spoofing (expected)</div>
                <div className="text-sm text-muted-foreground">DMARC is correctly blocking these emails</div>
              </button>
              <button
                onClick={() => dismissMutation.mutate({ item: dismissingItem, reason: "not_my_service" })}
                disabled={dismissMutation.isPending}
                className="w-full text-left px-4 py-3 rounded-lg border hover:bg-muted transition-colors"
              >
                <div className="font-medium">Not my service</div>
                <div className="text-sm text-muted-foreground">I don't use this service and don't need to fix it</div>
              </button>
              <button
                onClick={() => dismissMutation.mutate({ item: dismissingItem, reason: "other" })}
                disabled={dismissMutation.isPending}
                className="w-full text-left px-4 py-3 rounded-lg border hover:bg-muted transition-colors"
              >
                <div className="font-medium">Other</div>
                <div className="text-sm text-muted-foreground">I'll handle this another way</div>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setDismissingItem(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
